// src/features/ask/components/AskComposer.tsx
//
// The question box at the foot of the «Preguntar» panel: a multiline field
// (Enter sends, Shift+Enter breaks the line), the send button and the scope
// note that says which hotels the answer can see.
import React from 'react';
import { Box, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import SendRounded from '@mui/icons-material/SendRounded';
import LockOutlined from '@mui/icons-material/LockOutlined';
import { useTranslate } from 'react-admin';

export const ASK_MAX_QUESTION_LENGTH = 600;

export interface AskComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  busy: boolean;
  /** Hotels the person can see, once an answer has told us; null before. */
  hotelsInScope: number | null;
  inputRef?: React.Ref<HTMLTextAreaElement>;
}

export const AskComposer: React.FC<AskComposerProps> = ({ value, onChange, onSubmit, busy, hotelsInScope, inputRef }) => {
  const translate = useTranslate();
  const tooLong = value.length > ASK_MAX_QUESTION_LENGTH;
  const canSend = !busy && value.trim().length > 0 && !tooLong;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      if (canSend) onSubmit();
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
        <TextField
          inputRef={inputRef}
          placeholder={translate('resources.ask.composer.placeholder')}
          inputProps={{ 'aria-label': translate('resources.ask.composer.label') }}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={handleKeyDown}
          multiline
          minRows={1}
          maxRows={5}
          fullWidth
          size="small"
          error={tooLong}
          helperText={tooLong ? translate('resources.ask.composer.tooLong', { max: ASK_MAX_QUESTION_LENGTH }) : undefined}
        />
        <Tooltip title={translate('resources.ask.composer.send')}>
          <span>
            <IconButton
              color="primary"
              onClick={onSubmit}
              disabled={!canSend}
              aria-label={translate('resources.ask.composer.send')}
              sx={{ width: 44, height: 44 }}
            >
              <SendRounded />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
        <LockOutlined sx={{ fontSize: 14 }} aria-hidden />
        <Typography variant="caption">
          {hotelsInScope === null
            ? translate('resources.ask.scope.generic')
            : translate('resources.ask.scope.count', { smart_count: hotelsInScope })}
        </Typography>
      </Box>
    </Box>
  );
};

export default AskComposer;
